import merge from 'lodash.merge';
import instance from './base.service';

const MENU_REFRESH_RATE_MS = 10000;
const MAX_GENERATION_ATTEMPTS = 60;

const getHandlerKey = (contentRequestId, requestTryNumber) =>
  `${contentRequestId}-${requestTryNumber}`;
const checkRequestAbortHandlers = new Map();
const checkTimeoutAbortHandlers = new Map();

export const abortOngoingGenerationStatusChecks = (contentRequestId, requestTryNumber) => {
  const currentKey =
    contentRequestId && requestTryNumber ? getHandlerKey(contentRequestId, requestTryNumber) : null;

  [checkRequestAbortHandlers, checkTimeoutAbortHandlers].forEach((handlers) => {
    handlers.forEach((handler, key) => {
      if (key === currentKey) {
        return;
      }

      handler();
      handlers.delete(key);
    });
  });
};

// with 60 attempts at each 10 seconds, we will have a window of 10 min to get the update from the API
const checkGenerationStatus = async (params, attempt, timeout) => {
  if (attempt > MAX_GENERATION_ATTEMPTS) {
    throw new Error('Maximum attempts reached');
  }

  const { contentRequestId, lastRequestTryNumber: requestTryNumber } = params;
  const handlerKey = getHandlerKey(contentRequestId, requestTryNumber);
  const abortController = new AbortController();

  checkRequestAbortHandlers.set(handlerKey, () => abortController.abort());

  const response = await instance.get(
    `/content-requests/${contentRequestId}/tries/${requestTryNumber}/generated-content`,
    {
      signal: abortController.signal
    }
  );
  const status = response.data.status;

  if (['success', 'needsattention', 'failed'].includes(status)) {
    return response;
  } else if (status === 'inprogress') {
    let timeoutId;
    checkTimeoutAbortHandlers.set(handlerKey, () => clearTimeout(timeoutId));

    await new Promise((resolve) => {
      timeoutId = setTimeout(resolve, timeout);
    });

    return checkGenerationStatus(params, attempt + 1, timeout);
  } else {
    throw new Error(`Unknown request status: ${status}`);
  }
};

const cleanUpData = (data) => {
  const cleanedUpData = merge({}, data);

  if (data.dealershipAccount?.id === '') {
    delete cleanedUpData.dealershipAccount;
  }

  if (data.vehicle?.year === '') {
    delete cleanedUpData.vehicle;
  }

  return cleanedUpData;
};

export const generateContent = async (data) => {
  const response = await instance.post('/content-requests', cleanUpData(data));
  return response.data;
};

export const regenerateContent = async (data) => {
  const { contentRequestId } = data;

  const response = await instance.post(
    `/content-requests/${contentRequestId}/tries`,
    cleanUpData(data)
  );
  return response.data;
};

export const retrieveGeneratedContent = async (
  params,
  attempt = 0,
  timeout = MENU_REFRESH_RATE_MS
) => {
  const response = await checkGenerationStatus(params, attempt, timeout);
  return response.data;
};

export const updateSummaryContentRequestId = async (params) => {
  const { contentRequestId, requestTryNumber, summaryContentRequestId } = params;
  const url = `/content-requests/${contentRequestId}/tries/${requestTryNumber}/generated-content`;

  return await instance.patch(url, { summaryContentRequestId });
};

export const updateLastUsageDate = async (params) => {
  const { contentRequestId, requestTryNumber, lastUsageDate } = params;
  const url = `/content-requests/${contentRequestId}/tries/${requestTryNumber}/generated-content`;

  return await instance.patch(url, { lastUsageDate });
};
